import { List, useTable } from '@refinedev/antd';
import type { CrudFilters } from '@refinedev/core';
import { Table, Form, DatePicker, Input, Select, Button, Space, Tag, Tooltip } from 'antd';
import { Link, useParams } from 'react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BidiText, MonoCode, formatDateBidi } from '@lebinatikra/rtl-utils';
import { DecisionTag } from './decision-tag';
import { ActionModal } from '../moderation-queue/action-modal';

interface AuditLogRow {
  id: string;
  timestamp: string;
  action: string;
  filter_decision: string | null;
  user_id: string | null;
  community_id: string;
  organization_id: string | null;
  moderation_decision: string | null;
}

interface SearchForm {
  dateRange?: [unknown, unknown];
  action?: string;
  user_id?: string;
  filter_decision?: string;
}

const DECISION_KEYS = ['allow', 'block_pre_filter', 'block_post_filter', 'rewrite'] as const;
const BLOCK_DECISIONS = ['block_pre_filter', 'block_post_filter'];

// A row can be taken into moderation only when the filter blocked it and no
// moderator has ruled on it yet — same gate as the moderation queue.
function isActionable(r: AuditLogRow): boolean {
  return BLOCK_DECISIONS.includes(r.filter_decision ?? '') && !r.moderation_decision;
}

export function AuditLogList({ scope }: { scope: 'community' | 'organization' }) {
  const { t } = useTranslation();
  const decisionOptions = DECISION_KEYS.map((k) => ({ value: k, label: t(`auditLog.decision.${k}`) }));
  const { communityId, organizationId } = useParams();
  const parentId = scope === 'community' ? communityId : organizationId;
  const [modalRecord, setModalRecord] = useState<AuditLogRow | null>(null);

  const { tableProps, searchFormProps } = useTable<AuditLogRow>({
    resource: 'audit-log',
    pagination: { pageSize: 100 },
    sorters: { initial: [{ field: 'timestamp', order: 'desc' }] },
    meta: { parentId, scope },
    syncWithLocation: true,
    onSearch: (data): CrudFilters => {
      const { dateRange, action, user_id, filter_decision } = data as SearchForm;
      const filters: CrudFilters = [];
      if (dateRange?.[0]) {
        filters.push({
          field: 'timestamp_from',
          operator: 'eq',
          value: (dateRange[0] as { toISOString: () => string }).toISOString(),
        });
      }
      if (dateRange?.[1]) {
        filters.push({
          field: 'timestamp_to',
          operator: 'eq',
          value: (dateRange[1] as { toISOString: () => string }).toISOString(),
        });
      }
      if (action?.trim()) {
        filters.push({ field: 'action', operator: 'eq', value: action.trim() });
      }
      if (user_id?.trim()) {
        filters.push({ field: 'user_id', operator: 'eq', value: user_id.trim() });
      }
      if (filter_decision) {
        filters.push({ field: 'filter_decision', operator: 'eq', value: filter_decision });
      }
      return filters;
    },
  });

  return (
    <List title={t('auditLog.title')}>
      <Form
        {...searchFormProps}
        layout="inline"
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item name="dateRange" label={t('auditLog.filters.timestamp')}>
          <DatePicker.RangePicker showTime />
        </Form.Item>
        <Form.Item name="action" label={t('auditLog.filters.action')}>
          <Input placeholder="filter.check" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="user_id" label={t('auditLog.filters.userId')}>
          <Input placeholder="UUID" allowClear style={{ width: 280 }} />
        </Form.Item>
        <Form.Item name="filter_decision" label={t('auditLog.filters.filterDecision')}>
          <Select
            allowClear
            placeholder={t('common.search')}
            options={decisionOptions}
            style={{ width: 160 }}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              {t('common.search')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
      <Table {...tableProps} rowKey="id">
        <Table.Column<AuditLogRow>
          dataIndex="timestamp"
          title={t('auditLog.columns.timestamp')}
          render={(v) => formatDateBidi(v)}
        />
        <Table.Column<AuditLogRow>
          dataIndex="action"
          title={t('auditLog.columns.action')}
          render={(v) => <BidiText>{v}</BidiText>}
        />
        <Table.Column<AuditLogRow>
          dataIndex="filter_decision"
          title={t('auditLog.columns.filterDecision')}
          render={(v) => <DecisionTag value={v} />}
        />
        <Table.Column<AuditLogRow>
          dataIndex="moderation_decision"
          title={t('auditLog.columns.moderationStatus')}
          render={(v: string | null, record) =>
            v ? (
              <Tag color="purple">{t(`moderationQueue.decision.${v}`)}</Tag>
            ) : BLOCK_DECISIONS.includes(record.filter_decision ?? '') ? (
              <Tag color="gold">{t('auditLog.moderationStatus.pending')}</Tag>
            ) : (
              '—'
            )
          }
        />
        <Table.Column<AuditLogRow>
          dataIndex="user_id"
          title={t('auditLog.columns.userId')}
          render={(v: string | null) =>
            v ? (
              <Tooltip title={v}>
                <MonoCode>{v.slice(0, 8)}…</MonoCode>
              </Tooltip>
            ) : (
              '—'
            )
          }
        />
        {scope === 'community' && (
          <Table.Column<AuditLogRow>
            dataIndex="organization_id"
            title={t('auditLog.columns.organization')}
            render={(v: string | null) =>
              v ? (
                <Tooltip title={v}>
                  <MonoCode>{v.slice(0, 8)}…</MonoCode>
                </Tooltip>
              ) : (
                '—'
              )
            }
          />
        )}
        <Table.Column<AuditLogRow>
          title={t('common.actions')}
          render={(_, record) => (
            <Space>
              <Link to={`/audit-log/${record.id}`}>
                <Button type="link" size="small">
                  {t('common.view')}
                </Button>
              </Link>
              {isActionable(record) && (
                <Button type="primary" size="small" onClick={() => setModalRecord(record)}>
                  {t('moderationQueue.takeAction')}
                </Button>
              )}
            </Space>
          )}
        />
      </Table>
      <ActionModal
        record={modalRecord}
        open={!!modalRecord}
        onClose={() => setModalRecord(null)}
      />
    </List>
  );
}
